<template>
    <BaseDialog
        v-model:open="open"
        title="Review completed action"
        description="Share how this development action went."
        :show-close="!isSubmitting"
        float-close
        :disable-close-on-backdrop-click="isSubmitting"
        dialog-content-class="p-5 sm:p-6 md:!max-w-lg">
        <form data-testid="development-action-feedback-dialog" class="flex flex-col gap-6" @submit.prevent="submit">
            <div class="flex flex-col gap-1 pr-8">
                <h2 class="title-subsection text-valence-grey-850">How did it go?</h2>
                <p class="body-small text-valence-grey-750">{{ actionTitle }}</p>
            </div>

            <fieldset :disabled="isSubmitting">
                <legend class="body-small mb-2 font-medium text-valence-grey-850">Choose an outcome</legend>
                <div class="grid grid-cols-3 gap-2">
                    <label v-for="option in outcomeOptions" :key="option.value" :for="`development-action-outcome-${option.value}`" class="cursor-pointer">
                        <input
                            :id="`development-action-outcome-${option.value}`"
                            v-model="outcome"
                            class="peer sr-only"
                            type="radio"
                            name="development-action-outcome"
                            :value="option.value" />
                        <span
                            class="body-small flex min-h-11 items-center justify-center rounded-lg border px-3 font-medium transition-colors peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-valence-primary-500"
                            :class="
                                outcome === option.value
                                    ? 'border-valence-primary-500 bg-valence-primary-100 text-valence-primary-600 ring-1 ring-valence-primary-500'
                                    : 'border-valence-grey-250 bg-white text-valence-grey-850 hover:bg-valence-grey-50'
                            ">
                            {{ option.label }}
                        </span>
                    </label>
                </div>
            </fieldset>

            <label for="development-action-feedback-learning" class="body-small flex flex-col gap-2 font-medium text-valence-grey-850">
                <span>What did you learn? <span class="font-normal text-valence-grey-750">(optional)</span></span>
                <BaseTextarea
                    id="development-action-feedback-learning"
                    v-model:input="whatLearned"
                    data-testid="development-action-feedback-learning"
                    aria-label="What did you learn?"
                    :maxlength="FEEDBACK_TEXT_MAX_LENGTH"
                    :disabled="isSubmitting"
                    disable-autosize
                    class="min-h-32 resize-y rounded-lg border border-valence-grey-250 bg-white p-3 text-valence-grey-850 transition-colors focus-visible:border-valence-primary-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-valence-primary-500/20" />
            </label>

            <p role="alert" aria-atomic="true" class="sr-only">{{ errorMessage }}</p>
            <p v-if="errorMessage" class="body-small text-red-700">{{ errorMessage }}</p>

            <div class="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
                <BaseButton theme="tertiary" :disabled="isSubmitting" @click="open = false">Skip and just mark done</BaseButton>
                <BaseButton theme="primary" class="base-btn-dark" :disabled="!canSubmit" :aria-busy="isSubmitting" @click="submit">
                    {{ isSubmitting ? "Submitting…" : "Submit review" }}
                </BaseButton>
            </div>
        </form>
    </BaseDialog>
</template>

<script setup lang="ts">
import type { DevelopmentActionFeedbackOutcome, DevelopmentActionFeedbackPayload } from "~vue/api/generated/feedback";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseTextarea from "~vue/components/form/BaseTextarea.vue";
import { computed, ref, watch } from "vue";

const FEEDBACK_TEXT_MAX_LENGTH = 2_000;
const outcomeOptions: { value: DevelopmentActionFeedbackOutcome; label: string }[] = [
    { value: "good", label: "Good" },
    { value: "okay", label: "Okay" },
    { value: "bad", label: "Bad" },
];

const props = defineProps<{
    actionTitle: string;
    isSubmitting: boolean;
    errorMessage: string | null;
}>();
const emit = defineEmits<{
    submit: [payload: DevelopmentActionFeedbackPayload];
}>();
const open = defineModel<boolean>("open", { default: false });
const outcome = ref<DevelopmentActionFeedbackOutcome | null>(null);
const whatLearned = ref("");
const canSubmit = computed(() => !props.isSubmitting && outcome.value !== null);

const submit = () => {
    if (!canSubmit.value || outcome.value === null) {
        return;
    }
    const learning = whatLearned.value.trim();
    emit("submit", {
        outcome: outcome.value,
        what_learned: learning || null,
    });
};

watch(open, (isOpen) => {
    if (!isOpen) {
        outcome.value = null;
        whatLearned.value = "";
    }
});
</script>
